import { useEffect, useState } from "react";
import { Loader2, LogOut, UserCog } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { returnToOwnAccount, useAuth } from "@/hooks/auth";
import { stopImpersonation } from "@/Api/impersonation";

/**
 * Faixa fixa exibida enquanto o usuário logado é uma sessão personificada.
 *
 * Sem ela o admin perde a noção de em nome de quem está agindo - e tudo o que
 * ele fizer fica registrado como sendo do usuário-alvo.
 */
export function ImpersonationBanner() {
  const auth = useAuth();
  const impersonation = auth?.impersonation;
  const expiresAtIso = impersonation?.expires_at ?? null;

  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!expiresAtIso) return;

    const interval = setInterval(() => setNow(Date.now()), 30000);

    return () => clearInterval(interval);
  }, [expiresAtIso]);

  if (!impersonation) return null;

  const expiresAt = expiresAtIso ? new Date(expiresAtIso) : null;
  const remainingMinutes = expiresAt
    ? Math.max(0, Math.floor((expiresAt.getTime() - now) / 60000))
    : null;

  const remainingLabel = () => {
    if (remainingMinutes === null) return null;
    if (remainingMinutes <= 0) return "expirando";

    const hours = Math.floor(remainingMinutes / 60);
    const minutes = remainingMinutes % 60;

    return hours > 0 ? `expira em ${hours}h${String(minutes).padStart(2, "0")}` : `expira em ${minutes} min`;
  };

  const handleReturn = async () => {
    setLoading(true);

    try {
      await stopImpersonation();
    } catch (error) {
      console.error("Erro ao encerrar o acesso como usuário:", error);
    } finally {
      /**
       * Mesmo se a revogação falhar, a conta personificada sai do navegador:
       * o token expira sozinho no backend.
       */
      returnToOwnAccount();
    }
  };

  return (
    <div className="sticky top-0 z-40 border-b border-amber-300/80 bg-amber-100/95 backdrop-blur dark:border-amber-800/60 dark:bg-amber-950/95">
      <div className="mx-auto flex w-full max-w-screen-2xl items-center justify-between gap-4 px-4 py-2">
        <div className="flex min-w-0 items-center gap-3">
          <span className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-200 text-amber-900 sm:flex dark:bg-amber-900/60 dark:text-amber-100">
            <UserCog className="h-4 w-4" />
          </span>

          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-amber-950 dark:text-amber-50">
              Você está acessando como {auth?.user?.name}
            </p>
            <p className="truncate text-xs text-amber-800/90 dark:text-amber-200/80">
              {auth?.user?.email}
              {impersonation.by?.name && <> · iniciado por {impersonation.by.name}</>}
              {expiresAt && (
                <>
                  {" "}· {remainingLabel()} (
                  {format(expiresAt, "HH:mm", { locale: ptBR })})
                </>
              )}
            </p>
          </div>
        </div>

        <Button
          size="sm"
          onClick={handleReturn}
          disabled={loading}
          className="shrink-0 gap-2 bg-amber-600 text-white shadow-sm hover:bg-amber-700 dark:bg-amber-500 dark:text-amber-950 dark:hover:bg-amber-400"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
          <span className="hidden sm:inline">Voltar para minha conta</span>
          <span className="sm:hidden">Voltar</span>
        </Button>
      </div>
    </div>
  );
}
